import React from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { ComparativeValue } from 'components/Analytics/constants';
import { formatNumber, getComparativeValue } from 'components/Analytics/utils';

interface FinanceTotalsProps {
    totals: string;
    growthpct: number;
}

export const FinanceTotals: React.FC<FinanceTotalsProps> = ({ totals, growthpct }) => {
    const comparativeValue = getComparativeValue(growthpct);
    const isNegative = comparativeValue === ComparativeValue.Less;
    const isPositive = comparativeValue === ComparativeValue.More;

    return (
        <Container>
            <Total>$ {formatNumber(totals) || '-'}</Total>
            <Growth $type={comparativeValue}>
                <span>{isNegative ? '-' : '+'}</span>
                <WrapperNumber>
                     <span>
                        {growthpct || '0'}
                    </span>
                    <span>%</span>
                    <span>
                        {isNegative && <Arrow color="#FF6565" direction="down" />}
                        {isPositive && <Arrow color="#1DB954" direction="up" />}
                    </span>
                </WrapperNumber>
                <Period>
                    <FormattedMessage
                        id="Finance.Totals.SamePeriod"
                        defaultMessage="in the first quarter"
                    />
                </Period>
            </Growth>
        </Container>
    );
};

const Arrow: React.FC<{ color: string; direction: 'up' | 'down' }> = ({ color, direction }) => (
    <ArrowIcon 
        width="16" 
        height="22" 
        viewBox="0 0 16 22" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
    >
        {direction === 'down' ? (
            <path d="M7.29289 21.7071C7.68342 22.0976 8.31658 22.0976 8.70711 21.7071L15.0711 15.3431C15.4616 14.9526 15.4616 14.3195 15.0711 13.9289C14.6805 13.5384 14.0474 13.5384 13.6569 13.9289L8 19.5858L2.34315 13.9289C1.95262 13.5384 1.31946 13.5384 0.928933 13.9289C0.538408 14.3195 0.538408 14.9526 0.928933 15.3431L7.29289 21.7071ZM8 0L7 4.37114e-08L7 21L8 21L9 21L9 -4.37114e-08L8 0Z" fill={color}/>
        ) : (
            <path d="M8.70711 0.292892C8.31658 -0.0976315 7.68342 -0.0976315 7.29289 0.292892L0.928932 6.65685C0.538408 7.04738 0.538408 7.68054 0.928932 8.07107C1.31946 8.46159 1.95262 8.46159 2.34315 8.07107L8 2.41421L13.6569 8.07107C14.0474 8.46159 14.6805 8.46159 15.0711 8.07107C15.4616 7.68054 15.4616 7.04738 15.0711 6.65685L8.70711 0.292892ZM8 22L9 22L9 1L8 1L7 1L7 22L8 22Z" fill={color}/>
        )}
    </ArrowIcon>
);

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 7px;

    @media (max-width: 768px) {
        gap: 5px;
    }
`;

const WrapperNumber = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
`;

const Total = styled.div`
    font-weight: 500;
    font-size: 30px;
    line-height: 33px;
    font-family: Outfit, sans-serif;

    @media (max-width: 768px) {
        font-size: 24px;
        line-height: 28px;
    }

    @media (max-width: 480px) {
        font-size: 20px;
        line-height: 24px;
    }
`;

const Growth = styled.div<{ $type: ComparativeValue }>`
    color: ${props => {
        if (props.$type === ComparativeValue.Less) return '#FF6565';
        if (props.$type === ComparativeValue.More) return '#1DB954';
        return '#000000';
    }};
    font-family: Outfit, sans-serif;
    font-weight: 500;
    font-size: 30px;
    line-height: 33px;
    display: flex;
    align-items: flex-end;
    gap: 1px;

    @media (max-width: 768px) {
        font-size: 24px;
        line-height: 28px;
    }

    @media (max-width: 480px) {
        font-size: 20px;
        line-height: 24px;
    }
`;

const Period = styled.div`
    font-family: SF Pro Display, sans-serif;
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;
    color: #00000080;

    @media (max-width: 768px) {
        font-size: 12px;
        line-height: 16px;
    }
`;

const ArrowIcon = styled.svg`
    @media (max-width: 768px) {
        width: 14px;
        height: 18px;
    }

    @media (max-width: 480px) {
        width: 12px;
        height: 16px;
    }
`;
